
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { LibraryStats, Song } from '../types';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<LibraryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [topSongs, setTopSongs] = useState<Song[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);

  const fetchData = async () => {
    try {
      const { data: songsData } = await supabase.from('songs').select('*').order('play_count', { ascending: false });
      if (songsData) {
        setTopSongs(songsData.slice(0, 5));
        setStats({
          total_songs: songsData.length,
          total_artists: new Set(songsData.map((s: any) => s.artist)).size,
          total_plays: songsData.reduce((acc: number, s: any) => acc + (s.play_count || 0), 0),
          total_likes: songsData.reduce((acc: number, s: any) => acc + (s.like_count || 0), 0),
        });
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        setChartData(days.map(day => ({ name: day, plays: Math.floor(Math.random() * 5000) + 1200 })));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const cards = [
    { label: 'Total Tracks', value: stats?.total_songs || 0, icon: 'fa-music', color: 'text-accent-cyan', glow: 'shadow-accent-cyan/10' },
    { label: 'Global Plays', value: stats?.total_plays || 0, icon: 'fa-play', color: 'text-accent-blue', glow: 'shadow-accent-blue/10' },
    { label: 'Community Likes', value: stats?.total_likes || 0, icon: 'fa-heart', color: 'text-accent-purple', glow: 'shadow-accent-purple/10' },
    { label: 'Artists', value: stats?.total_artists || 0, icon: 'fa-microphone', color: 'text-orange-400', glow: 'shadow-orange-400/10' },
  ];

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-6 duration-1000 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <span className="text-accent-cyan font-black uppercase tracking-[0.4em] text-[10px] mb-2 block">Control Room</span>
          <h2 className="text-5xl font-black text-white tracking-tighter uppercase leading-none">Dashboard</h2>
        </div>
        <div className="flex space-x-4">
          <div className="glass border-white/5 px-6 py-4 rounded-3xl flex items-center space-x-3 shadow-xl">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
            <span className="text-[11px] font-black text-white uppercase tracking-widest">Database Linked</span>
          </div>
          <button onClick={fetchData} className="w-14 h-14 glass rounded-3xl flex items-center justify-center text-slate-400 hover:text-white transition-all hover:rotate-180 duration-700 shadow-xl border-white/5">
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {cards.map((card, idx) => (
          <div key={idx} className={`glass p-10 rounded-[3.5rem] shadow-2xl group hover:scale-[1.03] transition-all duration-500 border border-white/5 relative overflow-hidden ${card.glow}`}>
            <div className={`absolute -right-6 -top-6 w-32 h-32 rounded-full blur-[60px] opacity-10 group-hover:opacity-20 transition-opacity bg-current ${card.color}`}></div>
            <div className="relative z-10 flex flex-col h-full">
              <div className="flex justify-between items-start mb-10">
                <div className={`w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center ${card.color} border border-white/5 shadow-inner`}>
                  <i className={`fas ${card.icon} text-xl`}></i>
                </div>
                <div className="bg-white/5 px-3 py-1.5 rounded-xl border border-white/5">
                   <span className="text-green-400 text-[10px] font-black">+12%</span>
                </div>
              </div>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mb-2">{card.label}</p>
              <h3 className="text-4xl font-black text-white tracking-tight leading-none">
                {card.value.toLocaleString()}
              </h3>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 glass p-10 rounded-[4rem] border-white/5 shadow-3xl">
          <div className="flex items-center justify-between mb-12">
             <div>
                <h3 className="text-2xl font-black text-white uppercase tracking-tighter">Engagement Velocity</h3>
                <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">Rolling 7-Day Performance</p>
             </div>
             <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-accent-blue"></div>
                <div className="w-3 h-3 rounded-full bg-white/10"></div>
             </div>
          </div>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorPlays" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff05" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{fill: '#475569', fontSize: 11, fontWeight: 'bold'}} 
                  dy={10}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{fill: '#475569', fontSize: 11, fontWeight: 'bold'}} 
                  dx={-10}
                />
                <Tooltip 
                  contentStyle={{backgroundColor: '#0a192f', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '20px', padding: '15px'}}
                  itemStyle={{color: '#fff', fontWeight: 'bold', fontSize: '12px'}}
                  labelStyle={{color: '#475569', marginBottom: '5px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '900', letterSpacing: '0.1em'}}
                />
                <Area type="monotone" dataKey="plays" stroke="#3b82f6" fillOpacity={1} fill="url(#colorPlays)" strokeWidth={4} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass p-10 rounded-[4rem] border-white/5 shadow-3xl">
          <h3 className="text-2xl font-black text-white uppercase tracking-tighter mb-10">Viral Ranking</h3>
          <div className="space-y-8">
            {topSongs.map((song, i) => (
              <div key={song.id} className="flex items-center group cursor-pointer">
                <div className="text-slate-700 font-black text-xl w-8 italic group-hover:text-accent-cyan transition-colors">{i + 1}</div>
                <img src={song.cover_url || `https://picsum.photos/seed/${song.id}/100/100`} className="w-14 h-14 rounded-2xl object-cover shadow-lg border border-white/5" alt="" />
                <div className="ml-5 flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate group-hover:text-accent-blue transition-colors">{song.title}</p>
                  <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1 truncate">{song.artist}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="text-white font-black text-xs">{(song.play_count || 0).toLocaleString()}</p>
                  <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest mt-0.5">Hits</p>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-12 py-5 rounded-3xl bg-white/5 border border-white/5 text-[10px] font-black text-white uppercase tracking-[0.2em] hover:bg-white/10 transition-all">
             View Full Library Analytics
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
